import type { Rng } from './rng';
import type { Tunables } from './tunables';

/** Quick Match state (GDD section 4). The player always has the ball; opponent drives are summarised. */
export interface MatchState {
  quarter: number;
  /** Seconds left in the quarter. */
  clock: number;
  home: number;
  away: number;
  over: boolean;
}

export function newMatch(t: Tunables): MatchState {
  return { quarter: 1, clock: t.match.quarterSeconds, home: 0, away: 0, over: false };
}

/** Runs the clock. Returns true when a quarter ended on this tick. */
export function tickClock(m: MatchState, seconds: number, t: Tunables): boolean {
  if (m.over) return false;
  m.clock = Math.max(0, m.clock - seconds);
  if (m.clock > 0) return false;
  if (m.quarter >= t.match.quarters) {
    m.over = true;
  } else {
    m.quarter++;
    m.clock = t.match.quarterSeconds;
  }
  return true;
}

export function clockText(m: MatchState): string {
  const s = Math.ceil(m.clock);
  return `Q${m.quarter} ${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export interface OpponentDrive {
  plays: number;
  yards: number;
  result: 'touchdown' | 'fieldGoal' | 'punt' | 'turnover';
  points: number;
  seconds: number;
}

/**
 * The opponent's possession, simulated instantly (GDD section 7, AI opponent drive).
 * Per play: expected yards = (offense - defense) / 10 + 4.5, sd 6 for passes and 3.5 for runs, 8 percent turnover chance.
 */
export function simulateOpponentDrive(rng: Rng, startBallOn: number, offense: number, defense: number, t: Tunables): OpponentDrive {
  const m = t.match;
  let ballOn = startBallOn;
  let down = 1;
  let toGo = 10;
  let plays = 0;
  const mean = m.oppBase + (offense - defense) * m.oppPerPoint;
  const turnover = Math.max(1, m.oppTurnoverChance - (offense - defense) * m.oppTurnoverPerPoint);
  // AI-concluded: the 8 percent is per drive, landing on a random play (per play made most drives turnovers).
  const turnoverOn = rng.roll(turnover) ? rng.int(1, 6) : -1;
  while (plays < m.oppMaxPlays) {
    plays++;
    if (plays === turnoverOn) return { plays, yards: ballOn - startBallOn, result: 'turnover', points: 0, seconds: plays * m.oppSecondsPerPlay };
    const sd = rng.roll(m.oppPassChance) ? m.oppPassSd : m.oppRunSd;
    const gain = Math.round(mean + sd * gaussian(rng));
    ballOn += gain;
    if (ballOn >= 100) return { plays, yards: 100 - startBallOn, result: 'touchdown', points: 7, seconds: plays * m.oppSecondsPerPlay };
    if (gain >= toGo) {
      down = 1;
      toGo = 10;
    } else {
      down++;
      toGo -= gain;
    }
    if (down === 4) {
      const kick = 100 - ballOn + m.fgSnapYards;
      if (kick <= fieldGoalRange(m.oppKickerKpw, t) && rng.roll(fieldGoalChance(kick, 0.1, t))) {
        return { plays, yards: ballOn - startBallOn, result: 'fieldGoal', points: 3, seconds: plays * m.oppSecondsPerPlay };
      }
      return { plays, yards: ballOn - startBallOn, result: 'punt', points: 0, seconds: plays * m.oppSecondsPerPlay };
    }
  }
  return { plays, yards: ballOn - startBallOn, result: 'punt', points: 0, seconds: plays * m.oppSecondsPerPlay };
}

/** Box-Muller from the seeded RNG. */
function gaussian(rng: Rng): number {
  const u = Math.max(1e-9, rng.next());
  const v = rng.next();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/** Field goal max range = 30 + 0.3 x KPW yards (GDD 6.8). */
export function fieldGoalRange(kpw: number, t: Tunables): number {
  return t.match.fgRangeBase + t.match.fgRangePerKpw * kpw;
}

/** Kick distance from the line of scrimmage: yards to the goal line plus the snap and the end zone. */
export function fieldGoalDistance(ballOn: number, t: Tunables): number {
  return 100 - ballOn + t.match.fgSnapYards;
}

/** madeChance = 95 - 1.2 x max(0, distance - 30) - 25 x offset x 10, clamp 5 to 98 (wind not modelled yet). */
export function fieldGoalChance(distance: number, sliderOffset: number, t: Tunables): number {
  const m = t.match;
  const c = m.fgMadeBase - m.fgPerYard * Math.max(0, distance - m.fgFreeYards) - 25 * sliderOffset * 10;
  return Math.min(m.fgMax, Math.max(m.fgMin, c));
}

/** Punt distance = 25 + 0.35 x KPW, plus noise. Returns the opponent's starting yard line (from their goal). */
export function puntResult(ballOn: number, kpw: number, rng: Rng, t: Tunables): number {
  const m = t.match;
  const dist = m.puntBase + m.puntPerKpw * kpw + rng.range(-m.puntNoise, m.puntNoise);
  const landing = ballOn + dist;
  if (landing >= 100) return m.touchbackBallOn;
  return Math.round(100 - landing);
}
